import { type Finding } from '@consigli/types';
import { ChevronDown, ChevronUp, File } from 'lucide-react';
import { type FC, type MouseEvent, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFindingMessage } from './use-finding-message';

const ICON_STROKE_WIDTH = 1.75;

interface FindingCardBodyProps {
  finding: Finding;
}

const isClamped = (element: HTMLElement | null) =>
  !!element && element.scrollHeight > element.clientHeight;

/**
 * What a finding looks like on a card, without the card.
 *
 * Shared by the pickable row in the add-context modal and the read-only row in
 * the sources modal, which the design draws identically - only the checkbox
 * around it differs.
 *
 * Two columns: the conflict message on the left and the extract it was raised
 * on to the right. Both are clamped, and one toggle opens both, so a long
 * message and a long extract never leave the reader with half of each.
 */
export const FindingCardBody: FC<FindingCardBodyProps> = ({ finding }) => {
  const { t } = useTranslation();
  const message = useFindingMessage(finding);
  const labels = finding.labels ?? [];

  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const messageRef = useRef<HTMLParagraphElement>(null);
  const extractRef = useRef<HTMLParagraphElement>(null);

  // Measured again on resize: the columns share the card's width, so the
  // modal narrowing can clamp text that fitted when it opened.
  useLayoutEffect(() => {
    if (isExpanded) return;
    const measure = () =>
      setIsTruncated(isClamped(messageRef.current) || isClamped(extractRef.current));
    measure();
    const observer = new ResizeObserver(measure);
    for (const element of [messageRef.current, extractRef.current]) {
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [isExpanded, message, finding.content]);

  // In the add-context modal the card is the checkbox's label, so the click
  // is kept from reaching it: opening the extract is not picking the finding.
  const toggleExpanded = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded((expanded) => !expanded);
  };

  return (
    <div className="flex min-w-0 flex-1 items-start gap-[var(--ds-space-9)]">
      <div className="flex min-w-0 flex-1 flex-col gap-[var(--ds-space-2)]">
        <p className="truncate text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)] text-[var(--ds-color-raw-slate-700)] first-letter:uppercase">
          {t(`dashboard.conflict-title.${finding.conflictType}`)}
        </p>
        <p
          ref={messageRef}
          className={`${isExpanded ? '' : 'line-clamp-3'} wrap-break-word text-[length:var(--ds-typography-heading-5-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-heading-5-line-height)] text-[var(--ds-color-text-primary)]`}
        >
          {message}
        </p>
        <div className="flex items-start gap-[var(--ds-space-1)]">
          <File
            aria-hidden="true"
            strokeWidth={ICON_STROKE_WIDTH}
            className="size-[var(--ds-space-4)] shrink-0 text-[var(--ds-color-raw-stone-600)]"
          />
          {/* One line in the design, so a longer filename ends in an ellipsis
              rather than taking a second and pushing the pill below it. */}
          <p className="min-w-0 truncate text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)] text-[var(--ds-color-raw-stone-600)]">
            {finding.fileName ?? t('findings.project-related-finding')}
          </p>
        </div>
        {labels.length > 0 && (
          /*
           * One row, and it does not wrap: the design gives the labels a
           * single 24px line. Past the width they are clipped, which is the
           * same bargain the pill's own `overflow-clip` makes with a long label.
           */
          <div className="flex shrink-0 gap-[var(--ds-space-1)] overflow-hidden">
            {labels.map((label) => (
              <span
                key={label}
                className="add-context-label-pill flex shrink-0 items-center justify-center overflow-hidden rounded-[var(--ds-radius-8)] bg-[var(--ds-color-bg-brand-light)] px-[var(--ds-space-2)] py-[var(--ds-space-1)] text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)] whitespace-nowrap"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-[var(--ds-space-2)]">
        <p className="text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)] text-[var(--ds-color-raw-slate-700)]">
          {t('findings.extract')}:
        </p>
        <p
          ref={extractRef}
          className={`${isExpanded ? '' : 'line-clamp-5'} wrap-break-word text-[length:var(--ds-typography-paragraph-small-font-size)] leading-[var(--ds-typography-paragraph-small-line-height)] text-[var(--ds-color-text-primary)]`}
        >
          {finding.content ? <q>{finding.content}</q> : t('findings.no-document-reference')}
        </p>
        {(isTruncated || isExpanded) && (
          <button
            type="button"
            aria-expanded={isExpanded}
            onClick={toggleExpanded}
            className="add-context-expand flex cursor-pointer items-center gap-[var(--ds-space-1)] self-start text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)] text-[var(--ds-color-text-secondary)] hover:text-[var(--ds-color-text-primary)]"
          >
            {t(isExpanded ? 'findings.show-less' : 'findings.show-more')}
            {isExpanded ? (
              <ChevronUp
                aria-hidden="true"
                strokeWidth={ICON_STROKE_WIDTH}
                className="size-[var(--ds-space-4)] shrink-0"
              />
            ) : (
              <ChevronDown
                aria-hidden="true"
                strokeWidth={ICON_STROKE_WIDTH}
                className="size-[var(--ds-space-4)] shrink-0"
              />
            )}
          </button>
        )}
      </div>
    </div>
  );
};
